// src/modules/app-views/services/pages/Document/tabs/AiInfoTab/ProcedureSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Tag, Typography, Descriptions, Collapse, theme } from 'antd';
import {
  ToolOutlined,
  CheckCircleOutlined,
  BookOutlined,
  ExclamationCircleOutlined,
  BulbOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import { DEFAULT_OPEN_KEYS } from './index';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * ProcedureSection - Displays the technical procedure from AI cataloging
 * Each subsection is a Collapse panel matching the page pattern
 */
const ProcedureSection = ({ aiResponse, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const proc = aiResponse?.procedure;
  if (!proc) return null;

  const hasContent = proc.overview || proc.steps?.length || proc.tools;
  if (!hasContent) return null;

  const hasTools = proc.tools?.physical?.length > 0 || proc.tools?.software?.length > 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Overview */}
      {proc.overview && (
        <Collapse
          className="collapsed-content-fit"

          defaultActiveKey={DEFAULT_OPEN_KEYS.includes('overview') ? ['overview'] : []}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'overview',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <FileTextOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('procedure_overview')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16, lineHeight: 1.6, color: 'var(--ant-color-text-secondary)' }}>
                  {proc.overview}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Tools */}
      {hasTools && (
        <Collapse
          className="collapsed-content-fit"

          defaultActiveKey={DEFAULT_OPEN_KEYS.includes('tools') ? ['tools'] : []}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'tools',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <ToolOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('tools_needed')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {proc.tools.physical?.length > 0 && (
                    <Descriptions
                      bordered
                      size="small"
                      column={1}
                      layout={isMobile ? 'vertical' : 'horizontal'}
                      styles={{ label: { fontWeight: 500 } }}
                      style={{ marginBottom: proc.tools.software?.length > 0 ? 16 : 0 }}
                    >
                      <Descriptions.Item label={translateX('physical_tools')}>
                        <Flex gap={4} wrap="wrap">
                          {proc.tools.physical.map((tool, i) => (
                            <Tag key={i} title={tool.description}>{tool.name}</Tag>
                          ))}
                        </Flex>
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                  {proc.tools.software?.length > 0 && (
                    <Descriptions
                      bordered
                      size="small"
                      column={1}
                      layout={isMobile ? 'vertical' : 'horizontal'}
                      styles={{ label: { fontWeight: 500 } }}
                    >
                      <Descriptions.Item label={translateX('software_tools')}>
                        <Flex gap={4} wrap="wrap">
                          {proc.tools.software.map((tool, i) => (
                            <Tag key={i} color="blue" title={tool.description}>{tool.name}</Tag>
                          ))}
                        </Flex>
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Pre-Checklist */}
      {proc.preChecklist?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"

          defaultActiveKey={DEFAULT_OPEN_KEYS.includes('pre-checklist') ? ['pre-checklist'] : []}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'pre-checklist',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <CheckCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('pre_service_checklist')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {proc.preChecklist.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < proc.preChecklist.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      <Text strong style={{ fontSize: 13 }}>
                        {i + 1}. {item.item}
                      </Text>
                      {item.detail && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                          {item.detail}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Steps */}
      {proc.steps?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"

          defaultActiveKey={DEFAULT_OPEN_KEYS.includes('steps') ? ['steps'] : []}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'steps',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <BookOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('step_by_step')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {proc.steps.map((step, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < proc.steps.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          {i + 1}. {step.phase}
                        </Text>
                        {step.duration && (
                          <Tag color="default" style={{ margin: 0, fontSize: 11 }}>
                            {step.duration}
                          </Tag>
                        )}
                      </Flex>
                      {step.description && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                          {step.description}
                        </Text>
                      )}
                      {step.actions?.length > 0 && (
                        <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                          {step.actions.map((action, j) => (
                            <li key={j} style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
                              {action}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Common Problems */}
      {proc.commonProblems?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"

          defaultActiveKey={DEFAULT_OPEN_KEYS.includes('common-problems') ? ['common-problems'] : []}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'common-problems',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <ExclamationCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('common_problems')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <Collapse
                    accordion
                    size="small"
                    items={proc.commonProblems.map((item, i) => ({
                      key: String(i),
                      label: (
                        <Text strong style={{ fontSize: 13 }}>
                          {i + 1}. {item.problem}
                        </Text>
                      ),
                      children: (
                        <div style={{ padding: 16 }}>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <strong>{translateX('cause')}:</strong>
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12, display: 'block', paddingLeft: 8 }}>
                            {item.cause}
                          </Text>
                        </div>
                        <div>
                          <Text style={{ fontSize: 12 }}>
                            <strong>{translateX('solution')}:</strong>
                          </Text>
                          <Text style={{ fontSize: 12, display: 'block', paddingLeft: 8 }}>
                            {item.solution}
                          </Text>
                        </div>
                      </div>
                    ),
                    }))}
                  />
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Tips */}
      {proc.tips?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"

          defaultActiveKey={DEFAULT_OPEN_KEYS.includes('tips') ? ['tips'] : []}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'tips',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <BulbOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('business_tips')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {proc.tips.map((tip, i) => (
                      <li key={i} style={{ marginBottom: 4, fontSize: 13 }}>{tip}</li>
                    ))}
                  </ul>
                </div>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProcedureSection;
